<template>
  <b-modal ref="cleaningModal" size="xl" no-close-on-backdrop scrollable
           modal-class="cm-modal" content-class="cm-content" body-class="cm-body"
           dialog-class="cm-dialog" header-class="cm-header" footer-class="cm-footer">
    <template #modal-header>
      <div class="cm-hero">
        <div class="cm-hero__top">
          <span class="cm-hero__eyebrow">{{ msg('Cleaning task') }}</span>
          <div class="cm-hero__top-right">
            <!-- Status dropdown — always visible because it lives in the
                 sticky header. Replaces the old in-body Status section so
                 users can change status from anywhere in the form. -->
            <b-dropdown
                v-if="currentStatus"
                size="sm"
                no-caret
                right
                toggle-class="cm-status-dd-toggle p-0 border-0 shadow-none"
                menu-class="cm-status-dd-menu"
            >
              <template #button-content>
                <span :class="`cm-status-pill cm-status-pill--${currentStatus.variant}`">
                  <feather-icon :icon="iconForStatus(request.status)" size="13" class="mr-50"/>
                  {{ currentStatus.label }}
                  <feather-icon icon="ChevronDownIcon" size="12" class="ml-50"/>
                </span>
              </template>
              <b-dropdown-item
                  v-for="opt in statusOptions"
                  :key="opt.value"
                  :active="request.status === opt.value"
                  @click="onStatusPicked(opt.value)"
              >
                <span :class="`cm-status-dd-row cm-status-dd-row--${opt.variant}`">
                  <feather-icon :icon="iconForStatus(opt.value)" size="14" class="mr-50"/>
                  {{ opt.label }}
                </span>
              </b-dropdown-item>
            </b-dropdown>
            <button class="cm-close" @click="$refs.cleaningModal.hide()" aria-label="Close">
              <feather-icon icon="XIcon" size="18"/>
            </button>
          </div>
        </div>
        <h2 class="cm-hero__title">{{ realEstate.fullAddress || msg('Unknown address') }}</h2>
        <div class="cm-hero__meta">
          <span class="cm-hero__chip">
            <feather-icon icon="UserIcon" size="13" class="mr-25"/>
            <a :href="`/real_estate/contracts/rent/${cleaning.contractId}`" target="_blank">
              {{ cleaning.clientName }}
            </a>
          </span>
          <span class="cm-hero__chip">
            <feather-icon icon="ClockIcon" size="13" class="mr-25"/>
            {{ cleaning.requestedCleanTime | moment('DD/MM/YY HH:mm') }}
          </span>
          <span v-if="cleaning.hasDamage" class="cm-hero__chip cm-hero__chip--danger">
            <feather-icon icon="AlertTriangleIcon" size="13" class="mr-25"/>
            {{ msg('Damage reported') }}
          </span>
        </div>
      </div>
    </template>

    <div class="cm-content-wrap">
      <!-- Cleaning report -->
      <section class="cm-section">
        <header class="cm-section__head">
          <span class="cm-section__icon cm-section__icon--info">
            <feather-icon icon="FileTextIcon" size="16"/>
          </span>
          <div>
            <h4>{{ msg('Cleaning Report') }}</h4>
            <p>{{ msg('Cleaner name and observations') }}</p>
          </div>
        </header>
        <div class="cm-section__body">
          <b-row>
            <b-col md="5">
              <label class="cm-label">
                <feather-icon icon="UserIcon" size="13" class="mr-25"/>{{ msg('Cleaner') }}
              </label>
              <b-form-input v-model="request.cleaner" :placeholder="msg('Enter cleaners name')"/>
            </b-col>
            <b-col md="7">
              <label class="cm-label">
                <feather-icon icon="MessageSquareIcon" size="13" class="mr-25"/>{{ msg('Notes') }}
              </label>
              <b-form-textarea v-model="request.notes" rows="3" :placeholder="msg('Anything worth mentioning…')"/>
            </b-col>
          </b-row>
        </div>
      </section>

      <!-- Photos -->
      <section class="cm-section" v-if="(isCleanedStatus || isCleaningInProgress)">
        <header class="cm-section__head">
          <span class="cm-section__icon cm-section__icon--gold">
            <feather-icon icon="CameraIcon" size="16"/>
          </span>
          <div>
            <h4>{{ msg('Cleaning Photos') }}</h4>
            <p>
              {{ (cleaning.images || []).length }}
              {{ ((cleaning.images || []).length === 1) ? msg('photo') : msg('photos') }} {{ msg('attached') }}
            </p>
          </div>
        </header>
        <div class="cm-section__body">
          <div v-if="Array.isArray(cleaning.images) && cleaning.images.length" class="cm-photos">
            <div v-for="img in cleaning.images" :key="img" class="cm-photo">
              <b-img :src="getUrl(`cleaning/image/${cleaning.id}/${img}`)" fluid thumbnail/>
            </div>
          </div>
          <vue-dropzone id="cleaning-modal-dropzone" class="cm-dropzone"
                        :options="dropzoneImagesOptions('task')"
                        :useCustomSlot="true"
                        :duplicateCheck="true"
                        @vdropzone-success="uploadImageSuccess">
            <div class="cm-dropzone__inner">
              <feather-icon icon="UploadCloudIcon" size="22" class="mb-25"/>
              <h5>{{ msg('Drag & drop or click to upload') }}</h5>
              <p>{{ msg('JPG, PNG, max 5 MB') }}</p>
            </div>
          </vue-dropzone>
        </div>
      </section>

      <!-- Resources -->
      <section class="cm-section">
        <header class="cm-section__head">
          <span class="cm-section__icon cm-section__icon--warning">
            <feather-icon icon="PackageIcon" size="16"/>
          </span>
          <div>
            <h4>{{ msg('Resources') }}</h4>
            <p>{{ msg('Compare original inventory vs. what is left after the stay.') }}</p>
          </div>
        </header>
        <div class="cm-section__body">
          <div class="cm-resources">
            <h5 class="cm-resources__title">{{ msg('Original Resources') }}</h5>
            <div class="cm-chip-grid" v-if="realEstate.resources && Object.keys(realEstate.resources).length">
              <span v-for="(value, key) in realEstate.resources" :key="key" class="cm-chip">
                <strong>{{ msg(formatResourceKey(key)) }}</strong>
                <span class="cm-chip__sep">·</span>
                <span class="cm-chip__val">{{ value }}</span>
              </span>
            </div>
            <div v-else class="cm-empty-inline">
              <feather-icon icon="InfoIcon" size="13" class="mr-25"/>
              {{ msg('No resources defined for this property.') }}
            </div>
          </div>
          <div class="cm-divider"></div>
          <div class="cm-resources">
            <h5 class="cm-resources__title">{{ msg('Resources after the stay') }}</h5>
            <cleaning-resources v-model="missingResources"/>
          </div>
        </div>
      </section>

      <!-- Damage -->
      <section class="cm-section" :class="{ 'cm-section--danger': hasDamage }">
        <header class="cm-section__head">
          <span class="cm-section__icon" :class="hasDamage ? 'cm-section__icon--danger' : 'cm-section__icon--muted'">
            <feather-icon icon="AlertTriangleIcon" size="16"/>
          </span>
          <div>
            <h4>{{ msg('Damage Report') }}</h4>
            <p>{{ hasDamage ? msg('Damage reported — describe and add photos') : msg('No damage to report') }}</p>
          </div>
          <div class="cm-section__actions">
            <div class="cm-toggle-group">
              <button
                  type="button"
                  class="cm-toggle cm-toggle--no"
                  :class="{ active: hasDamageGroup === 'N' }"
                  :disabled="cleaning.cleaned === true || resourceMissing"
                  @click="hasDamageGroup = 'N'"
              >
                <feather-icon icon="CheckIcon" size="13" class="mr-25"/>{{ msg('No damage') }}
              </button>
              <button
                  type="button"
                  class="cm-toggle cm-toggle--yes"
                  :class="{ active: hasDamageGroup === 'Y' }"
                  @click="hasDamageGroup = 'Y'"
              >
                <feather-icon icon="AlertTriangleIcon" size="13" class="mr-25"/>{{ msg('Damaged') }}
              </button>
            </div>
          </div>
        </header>
        <transition name="cm-fade">
          <div v-if="hasDamage" class="cm-section__body">
            <label class="cm-label">{{ msg('Damage description') }}</label>
            <b-form-textarea v-model="request.damageDescription" rows="3"
                             :placeholder="msg('What was damaged? Where? How bad?')"/>

            <div class="cm-photos mt-1" v-if="Array.isArray(damages.images) && damages.images.length">
              <div v-for="img in damages.images" :key="img" class="cm-photo">
                <b-img :src="getUrl(`damage/image/${damages.id}/${img}`)" fluid thumbnail/>
              </div>
            </div>

            <vue-dropzone id="damage-modal-dropzone" class="cm-dropzone cm-dropzone--danger mt-1"
                          :options="dropzoneImagesOptions('damage')"
                          :useCustomSlot="true"
                          :duplicateCheck="true"
                          @vdropzone-success="uploadImageSuccess">
              <div class="cm-dropzone__inner">
                <feather-icon icon="UploadCloudIcon" size="22" class="mb-25"/>
                <h5>{{ msg('Drag & drop damage photos here') }}</h5>
                <p>{{ msg('Show the damage clearly — JPG, PNG, max 5 MB') }}</p>
              </div>
            </vue-dropzone>
          </div>
        </transition>
      </section>
    </div>

    <template #modal-footer>
      <div class="cm-actions">
        <b-button variant="outline-secondary" @click="$refs.cleaningModal.hide()">
          <feather-icon icon="XIcon" size="14" class="mr-25"/>{{ msg('cancel') }}
        </b-button>
        <b-button variant="primary" :disabled="busy" @click="onSave">
          <b-spinner v-if="busy" small class="mr-25"/>
          <feather-icon v-else icon="SaveIcon" size="14" class="mr-25"/>
          {{ msg('Save') }}
        </b-button>
        <b-button variant="success" :disabled="busy" @click="trySaveCleaningTaskAndClose">
          <feather-icon icon="CheckIcon" size="14" class="mr-25"/>{{ msg('Save & Close') }}
        </b-button>
      </div>
    </template>
  </b-modal>
</template>
<script>
import {mapActions} from "vuex";
import vue2Dropzone from "vue2-dropzone";
import CleaningResources from "@/components/panels/realEstates/cleaningResources.vue";

const cleaningRequest = function () {
  return {
    status: null,
    notes: null,
    cleaner: null,
    hasDamage: false,
    damageDescription: null,
    missingResources: null
  }
}

export default {
  name: "cleaningEditModal",
  components: {CleaningResources, vueDropzone: vue2Dropzone,},
  data: () => ({
    busy: false,
    request: cleaningRequest(),
    cleaning: {},
    realEstate: {},
    damages: {},
    missingResources: {},
    resourceMissing: false
  }),
  computed: {
    statusEnum() {
      return this.$t('cleaningStatusEnum');
    },
    statusOptions() {
      return Object.keys(this.statusEnum).map(key => ({...this.statusEnum[key], value: key}));
    },
    currentStatus() {
      if (!this.request.status || !this.statusEnum[this.request.status]) return null;
      return this.statusEnum[this.request.status];
    },
    isCleaningInProgress() { return this.request.status === 'CLEANING_IN_PROGRESS'; },
    isCleanedStatus() { return this.request.status === 'CLEANED'; },
    hasDamage() { return this.cleaning.hasDamage || this.request.hasDamage; },
    hasDamageGroup: {
      get() { return this.request.hasDamage? "Y" : "N"; },
      set(value) { this.request.hasDamage = value === "Y"; }
    },
    dropzoneImagesOptions() {
      let $this = this;
      return function (type) {
        if ($this.cleaning == null || $this.cleaning.id == null) return {}
        return {
          url: window.config.apiUrl + `cleaning/update/${$this.cleaning.id}/upload/img/${type}`,
          headers: {'Authorization': `Bearer ${localStorage.Bearer}`},
          createImageThumbnails: false,
          thumbnailWidth: 50,
          thumbnailHeight: 50,
          addRemoveLinks: true,
          maxFilesize: 5000000,
          acceptedFiles: 'image/*',
        }
      }
    }
  },
  methods: {
    ...mapActions('cleaning', ['saveCleaningTask', 'fetchById']),
    showById(id) {
      let $this = this;
      this.fetchById(id).then(response => {
        let cleaningtask = response.CleaningTask;
        if (response.Damages != null) {
          cleaningtask.damageDescription = response.Damages.damageDescription;
        }
        $this.show(response.CleaningTask, response.RealEstate, response.Damages);
      });
    },
    formatResourceKey(key) {
      return key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase());
    },
    iconForStatus(status) {
      switch (status) {
        case 'NEED_CLEANING': return 'ClockIcon';
        case 'CLEANING_IN_PROGRESS': return 'LoaderIcon';
        case 'CLEANED': return 'CheckCircleIcon';
        case 'CANCELLED': return 'XCircleIcon';
        case 'OVERDUE': return 'AlertTriangleIcon';
        default: return 'CircleIcon';
      }
    },
    onStatusPicked(value) {
      this.request.status = value;
    },
    show(cleaning, realEstate, damages) {
      this.cleaning = {...cleaning};
      this.realEstate = {...realEstate};
      this.request = {...cleaningRequest(), ...cleaning};
      this.damages = damages || {};
      this.$refs.cleaningModal.show();
    },
    onSave() {
      this.busy = true;
      this.trySaveCleaningTask().finally(() => this.busy = false);
    },
    trySaveCleaningTask() {
      let $this = this;
      this.request.missingResources = this.cleaning.missingResources;
      return this.saveCleaningTask({id: this.cleaning.id, request: this.request}).then(response => {
        $this.cleaning = {...response.CleaningTask};
        $this.missingResources = $this.cleaning.missingResources;
        $this.damages = response.Damages || {};
        $this.$emit('updated', response.CleaningTask);
        $this.$toast.success($this.msg('Cleaning task saved'));
      }).catch((e) => {
        $this.$toast.error($this.msg('Save failed') + (e && e.message ? `: ${e.message}` : ''));
      });
    },
    uploadImageSuccess(file, response) {
      if (response == null || response.objects == null || response.objects['id'] == null)
        return;
      response = response.objects;
      let cleaningtask = response.CleaningTask;
      this.damages = response.Damages || {};
      this.cleaning.hasDamage = cleaningtask.hasDamage;
      this.cleaning.damageId = cleaningtask.damageId;
      this.cleaning.images = cleaningtask.images;
    },
    validateCleaning() {
      let $this = this;
      if (this.cleaning.hasMissingResources) {
        this.resourceMissing = this.cleaning.hasMissingResources != null && this.cleaning.hasMissingResources ? true : false;
        this.request.hasDamage = true;
      }
      return this.trySaveCleaningTask().then(() => $this.isCleanedStatus).catch(() => false);
    },
    trySaveCleaningTaskAndClose() {
      this.busy = true;
      this.validateCleaning().then((valid) => {
        if (valid) this.$refs.cleaningModal.hide();
      }).finally(() => this.busy = false);
    }
  }
}
</script>


<style lang="scss">
$cm-radius: 14px;
$cm-radius-lg: 18px;
$cm-border: rgba(60, 60, 70, 0.08);
$cm-border-dark: rgba(255, 255, 255, 0.06);
$cm-primary: #7367f0;
$cm-success: #28c76f;
$cm-warning: #ff9f43;
$cm-danger: #ea5455;
$cm-info: #00cfe8;
$cm-gold: #d4a96a;

/* Top-anchored modal with internal scrolling.
   PREVIOUS BUG: combining b-modal's `centered` with tall content pushed the
   top half of the dialog off-screen — the user couldn't see the hero or the
   status pill. Now we drop `centered` and clamp the dialog to viewport
   height: header pinned, body scrolls, footer pinned. */
.cm-modal .modal-dialog {
  max-width: 980px;
  margin: 1.75rem auto;
  height: calc(100vh - 3.5rem);
  display: flex;
  align-items: stretch;
}
.cm-content {
  border-radius: $cm-radius-lg;
  overflow: hidden;
  border: none;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.18), 0 8px 24px rgba(0, 0, 0, 0.08);
  width: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
}
.cm-header {
  padding: 0;
  border-bottom: 1px solid $cm-border;
  flex-shrink: 0;
}
.cm-body {
  padding: 0;
  overflow-y: auto;
  flex: 1 1 auto;
  min-height: 0; /* required for nested flex scroll */
}

/* Backdrop slightly stronger so the modal stands out */
.cm-modal ~ .modal-backdrop.show { opacity: 0.6; }

/* Mobile — let the modal use the full viewport */
@media (max-width: 700px) {
  .cm-modal .modal-dialog {
    margin: 0;
    height: 100vh;
    max-width: 100%;
  }
  .cm-content { border-radius: 0; }
}

/* ── Hero ───────────────────────────────────────── */
.cm-hero {
  width: 100%;
  background: linear-gradient(135deg, rgba(115, 103, 240, 0.08) 0%, rgba(0, 207, 232, 0.05) 100%);
  padding: 22px 26px 20px 26px;
}

.cm-hero__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.cm-hero__top-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.cm-hero__eyebrow {
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.18em;
  color: $cm-primary;
  text-transform: uppercase;
}

/* Old chip kept for compat — now using cm-status-pill */
.cm-hero__status {
  font-size: 0.78rem;
  font-weight: 700;
  padding: 5px 12px;
}

/* ── Status dropdown pill (replaces in-body Status section) ── */
::v-deep .cm-status-dd-toggle {
  background: transparent !important;
  padding: 0 !important;
}

::v-deep .cm-status-dd-toggle:focus,
::v-deep .cm-status-dd-toggle:active,
::v-deep .cm-status-dd-toggle:hover {
  background: transparent !important;
  box-shadow: none !important;
}

.cm-status-pill {
  display: inline-flex;
  align-items: center;
  font-size: 0.78rem;
  font-weight: 700;
  padding: 6px 12px;
  border-radius: 999px;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  white-space: nowrap;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 14px rgba(0, 0, 0, 0.18);
  }

  &--warning { background: $cm-warning; color: white; }
  &--info { background: $cm-info; color: white; }
  &--success { background: $cm-success; color: white; }
  &--danger { background: $cm-danger; color: white; }
  &--secondary { background: var(--secondary); color: white; }
  &--primary { background: $cm-primary; color: white; }
}

::v-deep .cm-status-dd-menu {
  border-radius: 12px !important;
  border: 1px solid $cm-border !important;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.14) !important;
  padding: 6px !important;
  min-width: 220px !important;

  .dropdown-item {
    border-radius: 8px;
    margin-bottom: 2px;
    padding: 8px 12px !important;
    font-weight: 700;
    font-size: 0.85rem;
    transition: background 0.12s ease;

    &:last-child { margin-bottom: 0; }
    &:hover { background: rgba(115, 103, 240, 0.08); }
    &.active {
      background: rgba(115, 103, 240, 0.14);
      font-weight: 800;
    }
  }
}

.cm-status-dd-row {
  display: inline-flex;
  align-items: center;

  &--warning { color: $cm-warning; }
  &--info { color: $cm-info; }
  &--success { color: $cm-success; }
  &--danger { color: $cm-danger; }
  &--secondary { color: var(--secondary); }
}

.cm-close {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.05);
  color: var(--secondary);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(234, 84, 85, 0.12);
    color: $cm-danger;
  }
}

.cm-hero__title {
  font-size: 1.5rem;
  font-weight: 800;
  margin: 0 0 10px 0;
  letter-spacing: -0.02em;
  line-height: 1.2;
}

.cm-hero__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.cm-hero__chip {
  display: inline-flex;
  align-items: center;
  background: rgba(115, 103, 240, 0.1);
  color: $cm-primary;
  font-size: 0.78rem;
  font-weight: 600;
  padding: 5px 11px;
  border-radius: 999px;

  a { color: inherit; text-decoration: none; }
  a:hover { text-decoration: underline; }

  &--success { background: rgba(40, 199, 111, 0.1); color: $cm-success; }
  &--danger { background: rgba(234, 84, 85, 0.1); color: $cm-danger; }
}

/* ── Body content ──────────────────────────────── */
.cm-content-wrap { padding: 18px 22px; }

.cm-section {
  background: var(--card-bg, white);
  border: 1px solid $cm-border;
  border-radius: $cm-radius-lg;
  margin-bottom: 12px;
  overflow: hidden;
  transition: box-shadow 0.18s ease, border-color 0.18s ease;

  &:hover { box-shadow: 0 6px 18px rgba(34, 41, 47, 0.05); }
  &--danger { border-color: rgba(234, 84, 85, 0.3); }
}

.cm-section__head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid $cm-border;

  h4 { font-size: 0.92rem; font-weight: 800; margin: 0; letter-spacing: -0.01em; }
  p { font-size: 0.74rem; color: var(--secondary); margin: 1px 0 0 0; font-weight: 500; }

  > div:not(.cm-section__actions) { flex: 1; min-width: 0; }
}

.cm-section__icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &--gold { background: rgba(212, 169, 106, 0.15); color: $cm-gold; }
  &--info { background: rgba(115, 103, 240, 0.12); color: $cm-primary; }
  &--warning { background: rgba(255, 159, 67, 0.12); color: $cm-warning; }
  &--danger { background: rgba(234, 84, 85, 0.12); color: $cm-danger; }
  &--muted { background: rgba(0, 0, 0, 0.04); color: var(--secondary); }
}

.cm-section__actions { flex-shrink: 0; }
.cm-section__body { padding: 14px 16px 16px 16px; }

.cm-label {
  display: flex;
  align-items: center;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--secondary);
  margin-bottom: 6px;
}

/* Status grid */
.cm-status-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 8px;
}

.cm-status-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 9px 12px;
  border: 1.5px solid $cm-border;
  border-radius: $cm-radius;
  background: transparent;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.18s ease;
  color: var(--secondary);

  &:hover { transform: translateY(-1px); }

  &--warning { color: $cm-warning; &.active { background: $cm-warning; color: white; border-color: $cm-warning; box-shadow: 0 6px 16px rgba(255, 159, 67, 0.32); } }
  &--info { color: $cm-info; &.active { background: $cm-info; color: white; border-color: $cm-info; box-shadow: 0 6px 16px rgba(0, 207, 232, 0.32); } }
  &--success { color: $cm-success; &.active { background: $cm-success; color: white; border-color: $cm-success; box-shadow: 0 6px 16px rgba(40, 199, 111, 0.32); } }
  &--danger { color: $cm-danger; &.active { background: $cm-danger; color: white; border-color: $cm-danger; box-shadow: 0 6px 16px rgba(234, 84, 85, 0.32); } }
  &--secondary { color: var(--secondary); &.active { background: var(--secondary); color: white; border-color: var(--secondary); } }
}

/* Damage toggle */
.cm-toggle-group {
  display: inline-flex;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 999px;
  padding: 3px;
}

.cm-toggle {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border: none;
  border-radius: 999px;
  background: transparent;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
  color: var(--secondary);
  transition: all 0.18s ease;

  &:disabled { opacity: 0.4; cursor: not-allowed; }
  &--no.active { background: $cm-success; color: white; box-shadow: 0 4px 12px rgba(40, 199, 111, 0.32); }
  &--yes.active { background: $cm-danger; color: white; box-shadow: 0 4px 12px rgba(234, 84, 85, 0.32); }
}

/* Photos */
.cm-photos {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 10px;
  margin-bottom: 12px;
}

.cm-photo .img-thumbnail {
  border-radius: 12px;
  border: 1px solid $cm-border;
  height: 110px;
  width: 100%;
  object-fit: cover;
  cursor: zoom-in;
  transition: transform 0.18s ease;

  &:hover { transform: scale(1.02); }
}

/* Dropzone */
.cm-dropzone {
  border: 2px dashed rgba(115, 103, 240, 0.3) !important;
  background: rgba(115, 103, 240, 0.03) !important;
  border-radius: $cm-radius !important;
  padding: 18px !important;
  min-height: 100px !important;
  cursor: pointer !important;
  transition: all 0.18s ease !important;

  &:hover { background: rgba(115, 103, 240, 0.07) !important; border-color: $cm-primary !important; }

  &--danger {
    border-color: rgba(234, 84, 85, 0.3) !important;
    background: rgba(234, 84, 85, 0.03) !important;

    &:hover { background: rgba(234, 84, 85, 0.07) !important; border-color: $cm-danger !important; }
    .cm-dropzone__inner ::v-deep .feather { color: $cm-danger; }
    .cm-dropzone__inner h5 { color: $cm-danger; }
  }

  .dz-message { margin: 0 !important; }
}

.cm-dropzone__inner {
  text-align: center;
  color: $cm-primary;

  h5 { margin: 4px 0 2px 0; font-weight: 700; font-size: 0.88rem; color: $cm-primary; }
  p { margin: 0; font-size: 0.72rem; color: var(--secondary); font-weight: 500; }
}

/* Resources */
.cm-resources__title { font-size: 0.85rem; font-weight: 800; letter-spacing: -0.01em; margin-bottom: 10px; }

.cm-chip-grid { display: flex; flex-wrap: wrap; gap: 6px; }

.cm-chip {
  display: inline-flex;
  align-items: center;
  padding: 5px 11px;
  background: rgba(115, 103, 240, 0.06);
  border: 1px solid rgba(115, 103, 240, 0.18);
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;

  strong { color: $cm-primary; font-weight: 700; }
}

.cm-chip__sep { margin: 0 4px; opacity: 0.5; }
.cm-chip__val { font-weight: 700; }

.cm-empty-inline {
  display: flex;
  align-items: center;
  font-size: 0.84rem;
  color: var(--secondary);
  padding: 8px 0;
}

.cm-divider { height: 1px; background: $cm-border; margin: 14px 0; }

/* Modal footer slot — always pinned at the bottom of the dialog */
::v-deep .cm-footer {
  padding: 12px 22px;
  border-top: 1px solid $cm-border;
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
}

.cm-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  width: 100%;
}

/* Transitions */
.cm-fade-enter-active, .cm-fade-leave-active { transition: all 0.22s ease; overflow: hidden; }
.cm-fade-enter, .cm-fade-leave-to { opacity: 0; max-height: 0; }
.cm-fade-enter-to, .cm-fade-leave { max-height: 1000px; }

/* Dark mode */
.dark-layout {
  // Modal content
  .cm-content { background: #283046; }
  .cm-header,
  .cm-actions { border-color: $cm-border-dark; }

  // Hero on dark
  .cm-hero {
    background: linear-gradient(135deg, rgba(115, 103, 240, 0.18) 0%, rgba(0, 207, 232, 0.10) 100%);
    color: rgba(255, 255, 255, 0.95);
  }
  .cm-hero__chip {
    background: rgba(115, 103, 240, 0.2);
  }
  .cm-close {
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.7);

    &:hover {
      background: rgba(234, 84, 85, 0.2);
      color: $cm-danger;
    }
  }

  // Sections + footer
  .cm-section { border-color: $cm-border-dark; background: #283046; }
  ::v-deep .cm-footer {
    border-top-color: $cm-border-dark;
    background: #283046;
  }
  .cm-section--danger { border-color: rgba(234, 84, 85, 0.4); }
  .cm-section__head { border-bottom-color: $cm-border-dark; }
  .cm-section__head h4 { color: rgba(255, 255, 255, 0.95); }
  .cm-section__head p { color: rgba(255, 255, 255, 0.55); }

  // Dividers + toggles
  .cm-divider { background: $cm-border-dark; }
  .cm-toggle-group { background: rgba(255, 255, 255, 0.06); }

  // Status buttons inactive state
  .cm-status-btn {
    border-color: $cm-border-dark;
    background: rgba(255, 255, 255, 0.02);
  }

  // Status dropdown menu on dark
  ::v-deep .cm-status-dd-menu {
    background: #283046 !important;
    border-color: $cm-border-dark !important;

    .dropdown-item {
      color: rgba(255, 255, 255, 0.85);

      &:hover {
        background: rgba(115, 103, 240, 0.16);
        color: white;
      }
      &.active {
        background: rgba(115, 103, 240, 0.22);
      }
    }
  }

  // Resource chips
  .cm-chip {
    background: rgba(115, 103, 240, 0.14);
    border-color: rgba(115, 103, 240, 0.3);
    color: rgba(255, 255, 255, 0.9);

    strong { color: #b8b1ff; }
  }
  .cm-empty-inline { color: rgba(255, 255, 255, 0.5); }
  .cm-resources__title { color: rgba(255, 255, 255, 0.95); }
  .cm-label { color: rgba(255, 255, 255, 0.55); }

  // Photos
  .cm-photo .img-thumbnail {
    background: rgba(255, 255, 255, 0.04);
    border-color: $cm-border-dark;
  }

  // Dropzones
  .cm-dropzone {
    background: rgba(115, 103, 240, 0.06) !important;
    border-color: rgba(115, 103, 240, 0.4) !important;

    &:hover {
      background: rgba(115, 103, 240, 0.12) !important;
    }
  }
  .cm-dropzone--danger {
    background: rgba(234, 84, 85, 0.06) !important;
    border-color: rgba(234, 84, 85, 0.4) !important;

    &:hover {
      background: rgba(234, 84, 85, 0.12) !important;
    }
  }
}

@media (max-width: 700px) {
  .cm-hero__title { font-size: 1.25rem; }
  .cm-section__head { flex-wrap: wrap; }
  .cm-section__actions { width: 100%; }
}

/* legacy hooks for old code paths */
.cleaning-edit-container .cleaning-dropzone { padding: 0; min-height: auto; }
.cleaning-edit-container .vue-form-wizard .wizard-tab-content { padding: 10px 5px; }
.original-resources { margin-bottom: 1px; }
.original-resources .row { margin: 0; }
.original-resources .col-12 { padding: 0 5px; }
</style>
